import React, { useState } from "react";
import {
  IconTarget,
  IconAward,
  IconReportAnalytics,
  IconPlus,
  IconSearch,
  IconStar,
  IconStarFilled,
  IconTrendingUp,
  IconProgress,
  IconAlertCircle,
  IconArrowUpRight,
  IconCheck,
  IconUsers,
} from "@tabler/icons-react";
import RoleToggle from "../../../components/RoleToggle";


interface Goal {
  id: string;
  employeeName: string;
  title: string;
  weightage: number; // e.g. 40%
  progress: number; // e.g. 75%
  dueDate: string;
  status: "On Track" | "At Risk" | "Completed" | "Delayed";
}

interface Appraisal {
  id: string;
  employeeName: string;
  role: string;
  selfRating: number;
  managerRating: number;
  lastReviewDate: string;
  status: "Draft" | "Pending Employee Sign" | "Completed" | "Under Review";
  metrics: {
    delivery: number;
    communication: number;
    collaboration: number;
    problemSolving: number;
    initiative: number;
  };
  notes: string;
}

const INITIAL_GOALS: Goal[] = [
  { id: "G-101", employeeName: "Vikram Malhotra", title: "Optimise last-mile shipping latency by 15%", weightage: 40, progress: 80, dueDate: "2026-06-30", status: "On Track" },
  { id: "G-102", employeeName: "Vikram Malhotra", title: "Refactor API Gateway error logging middleware", weightage: 30, progress: 95, dueDate: "2026-05-25", status: "On Track" },
  { id: "G-103", employeeName: "Rohan Das", title: "Complete migration of HRMS noticeboard components", weightage: 50, progress: 60, dueDate: "2026-06-15", status: "On Track" },
  { id: "G-104", employeeName: "Amit Sharma", title: "Audit warehouse inventory stock discrepancies", weightage: 60, progress: 40, dueDate: "2026-06-05", status: "At Risk" },
  { id: "G-105", employeeName: "Priya Patel", title: "Set up multi-branch payroll automated schedules", weightage: 40, progress: 100, dueDate: "2026-05-10", status: "Completed" },
  { id: "G-106", employeeName: "Rohan Das", title: "Implement darkstore shelf routing API tests", weightage: 30, progress: 15, dueDate: "2026-07-01", status: "Delayed" }
];

const INITIAL_APPRAISALS: Appraisal[] = [
  {
    id: "APR-201",
    employeeName: "Vikram Malhotra",
    role: "Senior Engineer",
    selfRating: 4.2,
    managerRating: 4.6,
    lastReviewDate: "2025-12-10",
    status: "Draft",
    metrics: { delivery: 5, communication: 4, collaboration: 5, problemSolving: 5, initiative: 4 },
    notes: "Vikram has demonstrated exceptional ownership in sorting out legacy API architecture. His leadership during the database migration was commendable."
  },
  {
    id: "APR-202",
    employeeName: "Rohan Das",
    role: "DevOps Engineer",
    selfRating: 3.8,
    managerRating: 3.9,
    lastReviewDate: "2025-12-12",
    status: "Completed",
    metrics: { delivery: 4, communication: 3, collaboration: 4, problemSolving: 4, initiative: 4 },
    notes: "Rohan successfully completed noticeboard setup and maintained high uptime. Needs to improve documentation."
  },
  {
    id: "APR-203",
    employeeName: "Priya Patel",
    role: "HR Executive",
    selfRating: 4.8,
    managerRating: 4.9,
    lastReviewDate: "2025-12-08",
    status: "Completed",
    metrics: { delivery: 5, communication: 5, collaboration: 5, problemSolving: 4, initiative: 5 },
    notes: "Outstanding recruitment and payroll coordination. Priya represents the gold standard of organization alignment."
  },
  {
    id: "APR-204",
    employeeName: "Amit Sharma",
    role: "QA Engineer",
    selfRating: 3.5,
    managerRating: 3.2,
    lastReviewDate: "2025-12-14",
    status: "Under Review",
    metrics: { delivery: 3, communication: 3, collaboration: 3, problemSolving: 4, initiative: 3 },
    notes: "Average delivery times have slipped slightly. Needs to re-focus on priority QA automation metrics."
  }
];

export default function PerformancePage() {
  const [activeTab, setActiveTab] = useState<"goals" | "appraisal" | "analytics">("goals");
  const [goals, setGoals] = useState<Goal[]>(INITIAL_GOALS);
  const [appraisals, setAppraisals] = useState<Appraisal[]>(INITIAL_APPRAISALS);

  const subViewTabs = [
    "Team Goals & OKRs",
    "Performance Appraisal Reviews",
    "Performance Analytics"
  ];
  const tabToViewMap: Record<string, "goals" | "appraisal" | "analytics"> = {
    "Team Goals & OKRs": "goals",
    "Performance Appraisal Reviews": "appraisal",
    "Performance Analytics": "analytics"
  };
  const viewToTabMap: Record<string, string> = {
    goals: "Team Goals & OKRs",
    appraisal: "Performance Appraisal Reviews",
    analytics: "Performance Analytics"
  };

  // Filters
  const [searchQuery, setSearchQuery] = useState("");

  // Goal Form State
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState<Partial<Goal>>({
    employeeName: "", title: "", weightage: 30, progress: 0, dueDate: "", status: "On Track"
  });

  // Appraisal Form State
  const [selectedAppraisalId, setSelectedAppraisalId] = useState("APR-201");
  const [activeAppraisal, setActiveAppraisal] = useState<Appraisal>(INITIAL_APPRAISALS[0]);

  // Handle appraisal changes
  const handleUpdateMetric = (metricKey: keyof Appraisal["metrics"], value: number) => {
    const updatedMetrics = { ...activeAppraisal.metrics, [metricKey]: value };
    
    // Calculate average rating based on 5 metrics
    const avg = Object.values(updatedMetrics).reduce((a, b) => a + b, 0) / 5;
    
    const updatedAppraisal = {
      ...activeAppraisal,
      metrics: updatedMetrics,
      managerRating: parseFloat(avg.toFixed(1))
    };

    setActiveAppraisal(updatedAppraisal);
    
    // Sync with main array
    setAppraisals(prev => prev.map(a => a.id === activeAppraisal.id ? updatedAppraisal : a));
  };

  const handleUpdateNotes = (notesText: string) => {
    const updatedAppraisal = { ...activeAppraisal, notes: notesText };
    setActiveAppraisal(updatedAppraisal);
    setAppraisals(prev => prev.map(a => a.id === activeAppraisal.id ? updatedAppraisal : a));
  };

  const handleSelectAppraisal = (id: string) => {
    setSelectedAppraisalId(id);
    const found = appraisals.find(a => a.id === id);
    if (found) setActiveAppraisal(found);
  };

  // Appraisal Hike Recommendation Engine
  // Scores: Outstanding (4.5 - 5.0), Exceeds (3.8 - 4.4), Meets (3.0 - 3.7), Below ( < 3.0 )
  const getHikeRecommendation = (score: number) => {
    if (score >= 4.5) return { pct: "12% - 15%", grade: "A+ Outstanding Performance", color: "text-green-600 bg-green-50 border-green-200" };
    if (score >= 3.8) return { pct: "8% - 12%", grade: "A Exceeds Expectations", color: "text-blue-600 bg-blue-50 border-blue-200" };
    if (score >= 3.0) return { pct: "5% - 8%", grade: "B Meets Expectations", color: "text-amber-600 bg-amber-50 border-amber-200" };
    return { pct: "0% (PIP Recommended)", grade: "C Needs Performance Plan", color: "text-red-600 bg-red-50 border-red-200" };
  };

  const recommendation = getHikeRecommendation(activeAppraisal.managerRating);

  // Action: Add Goal
  const handleAddGoal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGoal.employeeName || !newGoal.title || !newGoal.dueDate) return;

    const added: Goal = {
      id: `G-${100 + goals.length + 1}`,
      employeeName: newGoal.employeeName,
      title: newGoal.title,
      weightage: newGoal.weightage || 20,
      progress: newGoal.progress || 0,
      dueDate: newGoal.dueDate,
      status: "On Track"
    };

    setGoals([...goals, added]);
    setShowGoalModal(false);
    setNewGoal({ employeeName: "", title: "", weightage: 30, progress: 0, dueDate: "", status: "On Track" });
  };

  // Action: Update Progress
  const handleIncrementProgress = (goalId: string) => {
    setGoals(prev => prev.map(g => {
      if (g.id === goalId) {
        const nextProgress = Math.min(g.progress + 10, 100);
        return {
          ...g,
          progress: nextProgress,
          status: nextProgress === 100 ? "Completed" : g.status
        };
      }
      return g;
    }));
  };

  // Action: Complete Appraisal
  const handleCompleteAppraisal = () => {
    const completedAppraisal = { ...activeAppraisal, status: "Completed" as Appraisal["status"] };
    setActiveAppraisal(completedAppraisal);
    setAppraisals(prev => prev.map(a => a.id === activeAppraisal.id ? completedAppraisal : a));
  };

  return (
    <div className="w-full mx-auto mt-2 animate-in fade-in duration-300">
      
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212121]">Performance & Goal Tracking</h1>
          <p className="text-xs text-gray-500 mt-0.5">Define team Objectives and Key Results (OKRs), perform manager appraisals, and preview compensation recommendations</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "goals" && (
            <button 
              onClick={() => setShowGoalModal(true)}
              className="bg-[#FBD037] text-[#212121] px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm font-semibold hover:bg-[#FACC15] transition shadow-sm"
            >
              <IconPlus size={16} /> Add Employee Goal
            </button>
          )}
        </div>
      </div>


      {/* Navigation tabs */}
      <div className="flex items-center justify-between mb-6">
        <RoleToggle
          options={subViewTabs}
          active={viewToTabMap[activeTab]}
          onChange={(tab) => setActiveTab(tabToViewMap[tab])}
        />
      </div>

      {/* Tab content */}
      {activeTab === "goals" && (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="bg-white p-4 border border-gray-100 rounded-2xl shadow-sm flex items-center justify-between">
            <div className="relative w-64">
              <IconSearch size={16} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search goals by employee..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 w-full text-xs border border-gray-200 rounded-xl outline-none focus:border-[#FBD037] font-medium"
              />
            </div>
            <span className="text-[10px] text-gray-400 font-mono">ACTIVE TARGET GOALS</span>
          </div>

          {/* Goals Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {goals
              .filter(g => g.employeeName.toLowerCase().includes(searchQuery.toLowerCase()))
              .map(goal => (
                <div key={goal.id} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition flex flex-col justify-between">
                  <div>
                    {/* Goal Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                          {goal.id}
                        </span>
                        <strong className="text-xs text-gray-700 block mt-1">{goal.employeeName}</strong>
                      </div>

                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${
                        goal.status === "Completed"
                          ? "bg-green-50 text-green-700 border-green-200"
                          : goal.status === "On Track"
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : goal.status === "At Risk"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-red-50 text-red-700 border-red-200"
                      }`}>
                        {goal.status}
                      </span>
                    </div>

                    <h3 className="font-bold text-gray-800 text-xs mt-1 leading-normal">
                      {goal.title}
                    </h3>
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-50">
                    <div className="flex justify-between items-center text-[10px] text-gray-500 mb-1.5 font-medium">
                      <span>Goal Weight: <strong className="text-gray-800">{goal.weightage}%</strong></span>
                      <span>Progress: <strong className="text-gray-800">{goal.progress}%</strong></span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                      <div 
                        className={`h-full rounded-full transition-all duration-300 ${
                          goal.progress === 100 
                            ? "bg-green-500" 
                            : goal.status === "At Risk"
                            ? "bg-amber-500"
                            : "bg-[#FBD037]"
                        }`}
                        style={{ width: `${goal.progress}%` }}
                      />
                    </div>

                    <div className="flex justify-between items-center pt-1">
                      <span className="text-[9px] text-gray-400 font-mono">Due: {goal.dueDate}</span>
                      {goal.progress < 100 && (
                        <button
                          onClick={() => handleIncrementProgress(goal.id)}
                          className="text-[9px] bg-gray-50 hover:bg-gray-100 text-gray-700 font-bold px-2 py-1 rounded-lg border border-gray-100 transition flex items-center gap-1"
                        >
                          <IconProgress size={10} /> +10% Progress
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Tab: Appraisal Review */}
      {activeTab === "appraisal" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Appraisal List Sidebar */}
          <div className="lg:col-span-4 bg-white border border-gray-100 rounded-2xl shadow-sm p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Pending Appraisals</h3>
            <div className="space-y-2">
              {appraisals.map((appr) => (
                <div
                  key={appr.id}
                  onClick={() => handleSelectAppraisal(appr.id)}
                  className={`p-3 rounded-xl border transition cursor-pointer flex items-center justify-between ${
                    selectedAppraisalId === appr.id
                      ? "border-[#FBD037] bg-amber-50/10 shadow-xs"
                      : "border-gray-100 hover:border-gray-200"
                  }`}
                >
                  <div>
                    <h4 className="font-bold text-xs text-gray-800">{appr.employeeName}</h4>
                    <p className="text-[10px] text-gray-400">{appr.role}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-gray-700 block">⭐ {appr.managerRating}</span>
                    <span className="text-[8px] text-gray-400 font-mono">Status: {appr.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Appraisal Form & Details */}
          <div className="lg:col-span-8 bg-white border border-gray-100 rounded-2xl shadow-sm p-5 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <div>
                <h2 className="font-bold text-gray-800 text-sm">{activeAppraisal.employeeName}</h2>
                <p className="text-xs text-gray-400 font-medium">{activeAppraisal.role} | Appraisal Reference: {activeAppraisal.id}</p>
              </div>
              <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${
                activeAppraisal.status === "Completed"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}>
                {activeAppraisal.status}
              </span>
            </div>

            {/* Appraisal Self vs Manager comparison */}
            <div className="grid grid-cols-2 gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
              <div>
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider">Self Appraisal Rating</span>
                <span className="block text-lg font-bold text-gray-700 mt-0.5">⭐ {activeAppraisal.selfRating} / 5.0</span>
              </div>
              <div className="border-l border-gray-200">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider">Manager Score</span>
                <span className="block text-lg font-bold text-gray-900 mt-0.5">⭐ {activeAppraisal.managerRating} / 5.0</span>
              </div>
            </div>

            {/* Appraisal Metric Slider Grids */}
            <div className="space-y-3 pt-2">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Manager Performance Assessment</h3>
              
              {[
                { key: "delivery", label: "Task Delivery & Velocity", desc: "Meets product deadlines, commits clean, testable code output" },
                { key: "communication", label: "Communication & Clarity", desc: "Collaborates across branches, writes clear documentation specs" },
                { key: "collaboration", label: "Teamwork & Synergy", desc: "Contributes effectively in daily meetings and cross-branch logistics alignment" },
                { key: "problemSolving", label: "Analytical & Problem Solving", desc: "Identifies system bottlenecks and architects scalable logic components" },
                { key: "initiative", label: "Proactivity & Ownership", desc: "Pushes features forward and acts as project champion" },
              ].map((metric) => {
                const val = activeAppraisal.metrics[metric.key as keyof Appraisal["metrics"]];
                return (
                  <div key={metric.key} className="border-b border-gray-50 pb-3 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                    <div className="max-w-md">
                      <span className="font-bold text-xs text-gray-800 block">{metric.label}</span>
                      <span className="text-[9px] text-gray-400 block">{metric.desc}</span>
                    </div>

                    {/* Interactive Star Buttons */}
                    <div className="flex gap-1 items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleUpdateMetric(metric.key as keyof Appraisal["metrics"], star)}
                          disabled={activeAppraisal.status === "Completed"}
                          className="text-amber-400 hover:scale-110 transition"
                        >
                          {star <= val ? <IconStarFilled size={18} /> : <IconStar size={18} />}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Managers Notes */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Manager Notes & Comments</label>
              <textarea
                value={activeAppraisal.notes}
                onChange={e => handleUpdateNotes(e.target.value)}
                disabled={activeAppraisal.status === "Completed"}
                placeholder="Write appraisal reviews, highlight key achievements, and mention scopes of improvement..."
                className="w-full p-2.5 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#FBD037] min-h-[80px]"
              />
            </div>

            {/* Compensation Recommendation Output */}
            <div className={`border rounded-xl p-3.5 flex items-start gap-3 mt-4 ${recommendation.color}`}>
              <IconAward size={22} className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider">Salary Review Recommendation</h4>
                <p className="text-[10px] font-medium leading-normal mt-0.5">
                  Based on the Manager score of <strong className="text-gray-900">⭐ {activeAppraisal.managerRating} / 5.0</strong>, the system recommends a salary increment of <strong className="text-gray-900">{recommendation.pct}</strong> under grade <strong className="text-gray-900">{recommendation.grade}</strong>.
                </p>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={handleCompleteAppraisal}
                disabled={activeAppraisal.status === "Completed"}
                className="w-full bg-[#212121] hover:bg-gray-800 text-white font-bold py-2.5 rounded-xl text-xs transition flex items-center justify-center gap-1.5 shadow-md"
              >
                <IconCheck size={14} /> Submit & Close Appraisal Review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Analytics & Leaderboards */}
      {activeTab === "analytics" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Top Performers (Left Panel) */}
          <div className="lg:col-span-5 bg-white border border-gray-100 rounded-2xl shadow-sm p-5 space-y-4">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1">
              <IconAward size={16} className="text-[#FBD037]" />
              Top Rated Organization Leaders
            </h3>

            <div className="space-y-3">
              {appraisals
                .sort((a, b) => b.managerRating - a.managerRating)
                .map((appr, idx) => (
                  <div key={appr.id} className="flex items-center justify-between border-b border-gray-50 pb-2.5 last:border-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#FBD037]/15 border border-[#FBD037]/30 flex items-center justify-center text-[10px] font-bold text-gray-700">
                        #{idx + 1}
                      </div>
                      <div>
                        <strong className="text-xs text-gray-800 block">{appr.employeeName}</strong>
                        <span className="text-[9px] text-gray-400">{appr.role}</span>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-xs font-bold text-gray-800 block">⭐ {appr.managerRating}</span>
                      <span className="text-[8px] text-green-600 bg-green-50 px-1.5 py-0.25 rounded-md font-bold">
                        {appr.managerRating >= 4.5 ? "A+ Grade" : appr.managerRating >= 3.8 ? "A Grade" : "B Grade"}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Org-Wide Performance Insights (Right Panel) */}
          <div className="lg:col-span-7 bg-gradient-to-br from-[#FFFFFD] to-[#FDF8E5] border border-[#FACC15]/20 rounded-2xl p-5 flex flex-col justify-between shadow-sm">
            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5 mb-4">
                <IconReportAnalytics size={16} className="text-amber-500" />
                Organization Performance Analytics
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-2xs">
                  <span className="text-[9px] text-gray-400 block font-bold uppercase tracking-wider">Average Team Rating</span>
                  <span className="text-2xl font-extrabold text-gray-800 block mt-1">⭐ 4.15 / 5.0</span>
                  <span className="text-[8.5px] text-green-600 font-semibold block mt-0.5 flex items-center gap-0.5">
                    <IconTrendingUp size={10} /> +2.4% vs last quarter
                  </span>
                </div>

                <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-2xs">
                  <span className="text-[9px] text-gray-400 block font-bold uppercase tracking-wider">Objective Velocity</span>
                  <span className="text-2xl font-extrabold text-gray-800 block mt-1">78.5%</span>
                  <span className="text-[8.5px] text-gray-400 block mt-0.5 font-medium">Goal Completion Rate</span>
                </div>
              </div>

              {/* Goal Distribution metrics */}
              <div className="mt-5 space-y-2">
                <span className="text-[9px] font-bold text-gray-500 uppercase block">Goals Completion Distribution</span>
                {[
                  { label: "On Track / Completed", count: 5, color: "bg-green-500", pct: 83 },
                  { label: "Delayed / At Risk", count: 1, color: "bg-amber-500", pct: 17 },
                ].map((dist) => (
                  <div key={dist.label} className="space-y-1">
                    <div className="flex justify-between text-[9px] text-gray-600 font-semibold">
                      <span>{dist.label}</span>
                      <span>{dist.count} ({dist.pct}%)</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${dist.color}`} style={{ width: `${dist.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-amber-200/40 text-[9px] text-gray-400 font-medium">
              Performance metrics conform to HR review directives for annual cycles.
            </div>
          </div>
        </div>
      )}

      {/* Goal Creation Modal */}
      {showGoalModal && (
        <div className="fixed inset-0 z-50 bg-[#212121]/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 border border-gray-100 shadow-xl space-y-4 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center border-b border-gray-100 pb-2">
              <h3 className="font-bold text-gray-900 text-sm">Add Employee Objective Goal</h3>
              <button 
                onClick={() => setShowGoalModal(false)}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddGoal} className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Assign to Employee</label>
                <select
                  value={newGoal.employeeName}
                  onChange={e => setNewGoal({ ...newGoal, employeeName: e.target.value })}
                  required
                  className="w-full p-2 border border-gray-200 rounded-xl text-xs outline-none bg-white focus:border-[#FBD037]"
                >
                  <option value="">-- Choose Employee --</option>
                  <option value="Vikram Malhotra">Vikram Malhotra</option>
                  <option value="Rohan Das">Rohan Das</option>
                  <option value="Amit Sharma">Amit Sharma</option>
                  <option value="Priya Patel">Priya Patel</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Goal / Objective Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Reduce server downtime by migrating to UDM SE Gateway"
                  value={newGoal.title}
                  onChange={e => setNewGoal({ ...newGoal, title: e.target.value })}
                  className="w-full p-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#FBD037]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Goal Weightage (%)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    placeholder="e.g. 40"
                    value={newGoal.weightage}
                    onChange={e => setNewGoal({ ...newGoal, weightage: parseInt(e.target.value) || 20 })}
                    className="w-full p-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#FBD037] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Target Due Date</label>
                  <input
                    type="date"
                    required
                    value={newGoal.dueDate}
                    onChange={e => setNewGoal({ ...newGoal, dueDate: e.target.value })}
                    className="w-full p-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#FBD037] font-mono"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowGoalModal(false)}
                  className="flex-1 py-2 border border-gray-200 font-bold rounded-xl text-xs hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#FBD037] hover:bg-[#FACC15] text-[#212121] font-bold py-2 rounded-xl text-xs transition"
                >
                  Create Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
